// ============================================================
// ContactPage.js
// Place in: frontend/src/pages/ContactPage.js
// CSS file:  frontend/src/styles/contactpage.css
// Route:     <Route path="/contact" element={<ContactPage />} />
// NOTE: Named ContactPage.js (not Contact.js) to avoid conflict
//       with the existing dashboard Contacts component.
// ============================================================

import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Login from './Login';
import API from '../services/api';
import '../styles/contactpage.css';

function ContactPage() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const isAuthenticated = !!localStorage.getItem('token');

    // Form state
    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '', newsletter: false });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [charCount, setCharCount] = useState(0);
        const [phoneError, setPhoneError] = useState('');

    // FAQ open state
    const [openFaq, setOpenFaq] = useState(null);

    // Scroll animation observer
    const observerRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('cp-visible'); }),
            { threshold: 0.1 }
        );
        document.querySelectorAll('.cp-animate').forEach(el => observerRef.current.observe(el));
        return () => observerRef.current?.disconnect();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'message') setCharCount(value.length);
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

        const subjectLabels = {
        sales: 'Sales Inquiry',
        support: 'Technical Support',
        billing: 'Billing Question',
        feedback: 'Feedback',
        other: 'Other'
    };

    const validatePhone = (value) => {
        if (!value.trim()) return ''; // optional field
        const cleaned = value.replace(/[\s-]/g, '');
        const isIndian = /^(\+91|91|0)?[6-9]\d{9}$/.test(cleaned);
        const isInternational = /^\+(?!91)[1-9]\d{7,14}$/.test(cleaned);
        return isIndian || isInternational
            ? ''
            : 'Please enter a valid mobile number (e.g. +91 9876543210)';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.firstName || !form.email || !form.subject || !form.message) {
            setError('Please fill in all required fields.');
            return;
        }
        const phoneMsg = validatePhone(form.phone);
        if (phoneMsg) {
            setPhoneError(phoneMsg);
            return;
        }
        setSending(true);
        try {
            await API.post('/auth/contact', {
                name: `${form.firstName} ${form.lastName}`.trim(),
                email: form.email,
                phone: form.phone.replace(/[\s-]/g, ''),
                useCase: subjectLabels[form.subject] || form.subject,
                message: form.message
            });
            setSent(true);
        } catch (err) {
            console.error('Submit contact inquiry error:', err);
            setError(err.response?.data?.message || 'Failed to send message. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const faqs = [
        { q: 'What are your support hours?', a: 'Our support team is available 24/7/365 — including weekends and public holidays. Live chat typically responds in under 2 minutes, email within 24 hours, and phone support is immediate.' },
        { q: 'How quickly will I receive a response?', a: 'Live chat: under 2 minutes. Phone: immediate. Email and contact form: within 24 hours, usually much faster. Priority plan holders receive faster SLA times on all channels.' },
        { q: 'Do you offer phone support?', a: 'Yes! Phone support is available 24/7 for all customers at +91 8860993121. Dedicated customers receive priority routing with reduced wait times.' },
        { q: 'Can I schedule a consultation?', a: 'Absolutely. Contact our sales team via email or phone to schedule a free 30-minute consultation. We\'ll discuss your requirements and recommend the best WhatsApp automation solution for your business.' },
        { q: 'What happens after I submit the contact form?', a: 'You\'ll receive an automatic confirmation email immediately. A member of the relevant team will then review your message and respond within 24 hours with a personalised reply.' },
    ];

    const channels = [
        { icon: '✉️', color: 'cyan', title: 'Email Us', desc: 'For general inquiries and support requests', links: ['support@chattech24.com', 'hello@chattech24.com'], tag: 'Replies within 24 hrs' },
        { icon: '📞', color: 'violet', title: 'Call Us', desc: 'Speak directly with our support engineers', links: ['+91 8860993121'], tag: '24 / 7 Available' },
        { icon: '💬', color: 'emerald', title: 'Live Chat', desc: 'Instant answers from our support team', tag: 'Avg. <2 min response', isChat: true },
        { icon: '📍', color: 'amber', title: 'Our Office', desc: 'Q-139, South City 1\nGurugram, Haryana — India', tag: 'Mon – Sat, 9am – 6pm IST', isMap: true },
    ];

    const departments = [
        { icon: '💼', color: 'cyan', title: 'Sales Team', desc: 'Pre-sales questions, custom solutions, and volume pricing', email: 'sales@chattech24.com', phone: '+91 8860993121' },
        { icon: '🛠️', color: 'violet', title: 'Technical Support', desc: 'Chatbot issues, API configs, flow errors, and 24/7 emergencies', email: 'support@chattech24.com', phone: '+91 8860993121' },
        { icon: '💳', color: 'emerald', title: 'Billing', desc: 'Invoices, refunds, subscription changes, and payment issues', email: 'billing@chattech24.com', phone: '+91 8860993121' },
        { icon: '🤝', color: 'amber', title: 'Partnerships', desc: 'Reseller programs, affiliate partnerships, and business collaborations', email: 'partners@chattech24.com', phone: '+91 8860993121' },
    ];

    const selfService = [
        { icon: '📚', color: 'cyan', title: 'Knowledge Base', desc: '500+ tutorials, step-by-step guides, and how-to articles covering every aspect of ChatTech24.' },
        { icon: '🎫', color: 'violet', title: 'Submit a Ticket', desc: 'Create a detailed support ticket for complex issues. Our engineers respond within 4 hours.' },
        { icon: '📊', color: 'emerald', title: 'System Status', desc: 'Real-time status of all our servers, networks, and WhatsApp API connections.' },
    ];

    return (
        <div className="cp-page">

            {/* ── NAVBAR ── */}
            <nav className={`ct-nav ${scrolled ? 'ct-nav-scrolled' : ''}`}>
                <div className="ct-nav-brand" onClick={() => navigate('/')}>
                    <img src="/logo.png" alt="ChatTech24" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
                </div>
                <div className="ct-nav-links">
                    <Link to="/how-it-works" className="ct-nav-link">How It Works</Link>
                    <Link to="/features" className="ct-nav-link">Features</Link>
                    <Link to="/services" className="ct-nav-link">Services</Link>
                    <Link to="/pricing" className="ct-nav-link">Pricing</Link>
                    <Link to="/contact" className="ct-nav-link" style={{ color: '#128C7E', fontWeight: 700 }}>Contact</Link>
                </div>
                <div className="ct-nav-actions">
                    {isAuthenticated ? (
                        <button onClick={() => navigate('/dashboard')} className="ct-btn-primary">Dashboard →</button>
                    ) : (
                        <>
                            <button onClick={() => setLoginModalOpen(true)} className="ct-btn-ghost">Login</button>
                            <button onClick={() => navigate('/pricing')} className="ct-btn-primary">Get Started</button>
                        </>
                    )}
                </div>
                <button className="ct-nav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                    <span /><span /><span />
                </button>
            </nav>

            {mobileMenuOpen && (
                <div className="ct-nav-mobile">
                    <div className="ct-mobile-menu-header">
                        <div
                            className="ct-mobile-menu-brand"
                            onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                            style={{ gap: '2px', cursor: 'pointer' }}
                        >
                            <img src="/logo 1.png" alt="ChatTech24" style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
                            <span style={{ color: '#D3D3D3', fontFamily: 'Sora, sans-serif', fontSize: '12px', fontWeight: '700' }}>ChatTech24</span>
                        </div>
                        <button className="ct-mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>✕</button>
                    </div>
                    <div className="ct-mobile-menu-links">
                        {[['⚙️', 'How It Works', '/how-it-works'], ['✨', 'Features', '/features'], ['🚀', 'Services', '/services'], ['💎', 'Pricing', '/pricing'], ['📞', 'Contact', '/contact']].map(([icon, label, to]) => (
                            <Link key={to} to={to} className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <span className="ct-mobile-link-icon">{icon}</span><span>{label}</span><span className="ct-mobile-link-arrow">›</span>
                            </Link>
                        ))}
                    </div>
                    <div className="ct-mobile-menu-actions">
                        <button className="ct-mobile-btn-login" onClick={() => { setLoginModalOpen(true); setMobileMenuOpen(false); }}>👤 Login</button>
                        <button className="ct-mobile-btn-cta" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}>🚀 Get Started</button>
                    </div>
                    <div className="ct-mobile-menu-footer">
                        <span>🔒 Secure & Reliable</span><span>⚡ 99.9% Uptime</span>
                    </div>
                </div>
            )}

            {/* ── HERO ── */}
            <div className="cp-hero">
                <div className="cp-hero-orb cp-orb-1" />
                <div className="cp-hero-orb cp-orb-2" />
                <div className="cp-hero-orb cp-orb-3" />
                <div className="cp-hero-grid" />
                <div className="cp-hero-inner">
                    <div className="cp-hero-badge">💬 We're Here to Help</div>
                    <h1 className="cp-hero-title">Get in Touch</h1>
                    <p className="cp-hero-sub">Have questions about ChatTech24? Our support team is available 24/7 to assist you with any inquiry — from setup to scale.</p>
                    <div className="cp-hero-trust">
                        <span>⚡ &lt;2 min live chat response</span>
                        <span>🔒 Secure & private</span>
                        <span>⭐ 5-star rated support</span>
                    </div>
                </div>
            </div>

            {/* ── QUICK CHANNELS ── */}
            <section className="cp-section cp-channels-section">
                <div className="cp-inner">
                    <div className="cp-channels-grid">
                        {channels.map((ch, i) => (
                            <div key={i} className={`cp-channel-card cp-channel-${ch.color} cp-animate`} style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className={`cp-channel-icon cp-icon-${ch.color}`}>{ch.icon}</div>
                                <h3 className="cp-channel-title">{ch.title}</h3>
                                <p className="cp-channel-desc">{ch.desc}</p>
                                {ch.links && ch.links.map((l, li) => (
                                    <a key={li} href={l.includes('@') ? `mailto:${l}` : `tel:${l.replace(/\s/g, '')}`} className="cp-channel-link">{l}</a>
                                ))}
                                {ch.isChat && (
                                    <button className="cp-channel-btn" onClick={() => alert('Live chat coming soon!')}>⚡ Start Chat</button>
                                )}
                                {ch.isMap && (
                                    <a href="https://maps.google.com/?q=South+City+1+Gurugram" target="_blank" rel="noopener noreferrer" className="cp-channel-link">View on Maps →</a>
                                )}
                                <div className="cp-channel-tag">{ch.tag}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CONTACT FORM + SIDEBAR ── */}
            <section className="cp-section cp-form-section" id="contact-form">
                <div className="cp-inner">
                    <div className="cp-form-grid">

                        {/* Sidebar */}
                        <div className="cp-sidebar cp-animate">
                            <span className="cp-eyebrow">✉️ Send a Message</span>
                            <h2 className="cp-sidebar-title">Let's Start a Conversation</h2>
                            <p className="cp-sidebar-lead">Fill out the form and our team will get back to you within 24 hours. For urgent matters, use live chat or phone.</p>

                            <div className="cp-sidebar-items">
                                {[
                                    { icon: '🕐', title: '24/7 Support', sub: "We're always available — even on weekends and holidays" },
                                    { icon: '🌐', title: 'Global Reliability', sub: 'WhatsApp API connections across multiple regions for 99.9% uptime' },
                                    { icon: '🏆', title: 'Award-Winning Support', sub: 'Rated #1 in customer satisfaction 3 years running' },
                                ].map((item, i) => (
                                    <div key={i} className="cp-sidebar-item">
                                        <div className="cp-sidebar-item-icon">{item.icon}</div>
                                        <div>
                                            <h4 className="cp-sidebar-item-title">{item.title}</h4>
                                            <p className="cp-sidebar-item-sub">{item.sub}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="cp-sidebar-social">
                                <h4 className="cp-sidebar-social-title">Follow Us</h4>
                                <div className="cp-socials">
                                    {['🌐', '🐦', '📸', '💼', '▶️'].map((s, i) => (
                                        <a key={i} href="#" className="cp-social">{s}</a>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="cp-form-wrap cp-animate" style={{ animationDelay: '0.15s' }}>
                            {error && (
                                <div className="cp-alert cp-alert-error">⚠️ {error}</div>
                            )}

                            {sent ? (
                                <div className="cp-success-panel">
                                    <div className="cp-success-icon">🎉</div>
                                    <h3 className="cp-success-title">Message Sent!</h3>
                                    <p className="cp-success-text">
                                        Thank you for reaching out! Our team will get back to you within <strong>24 hours</strong> at <strong>{form.email}</strong>. We've also sent a confirmation to your inbox.
                                    </p>
                                    <div className="cp-success-chips">
                                        <span>🕐 Response within 24 hrs</span>
                                        <span>✉️ Confirmation email sent</span>
                                    </div>
                                    <button onClick={() => { setSent(false); setForm({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '', newsletter: false }); setCharCount(0); }} className="cp-success-btn">
                                        ← Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form className="cp-form" onSubmit={handleSubmit} noValidate>
                                    <div className="cp-form-row">
                                        <div className="cp-form-group">
                                            <label className="cp-label">First Name <span className="cp-req">*</span></label>
                                            <div className="cp-input-wrap">
                                                <span className="cp-input-icon">👤</span>
                                                <input type="text" name="firstName" className="cp-input" placeholder="Rahul" value={form.firstName} onChange={handleChange} required />
                                            </div>
                                        </div>
                                        <div className="cp-form-group">
                                            <label className="cp-label">Last Name</label>
                                            <div className="cp-input-wrap">
                                                <span className="cp-input-icon">👤</span>
                                                <input type="text" name="lastName" className="cp-input" placeholder="Sharma" value={form.lastName} onChange={handleChange} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cp-form-group">
                                        <label className="cp-label">Email Address <span className="cp-req">*</span></label>
                                        <div className="cp-input-wrap">
                                            <span className="cp-input-icon">✉️</span>
                                            <input type="email" name="email" className="cp-input" placeholder="rahul@example.com" value={form.email} onChange={handleChange} required />
                                        </div>
                                    </div>

                                                                       <div className="cp-form-group">
                                        <label className="cp-label">Phone Number <span className="cp-opt">(optional)</span></label>
                                        <div className="cp-input-wrap">
                                            <span className="cp-input-icon">📞</span>
                                            <input
                                                type="tel"
                                                name="phone"
                                                inputMode="tel"
                                                maxLength={20}
                                                className={`cp-input ${phoneError ? 'cp-input-error' : ''}`}
                                                placeholder="+91 98765 43210"
                                                value={form.phone}
                                                onChange={e => {
                                                    const value = e.target.value.replace(/[^\d+\s-]/g, '');
                                                    setForm(prev => ({ ...prev, phone: value }));
                                                    if (phoneError) setPhoneError('');
                                                }}
                                                onBlur={() => setPhoneError(validatePhone(form.phone))}
                                            />
                                        </div>
                                        {phoneError && <span className="cp-field-error">{phoneError}</span>}
                                    </div>

                                    <div className="cp-form-group">
                                        <label className="cp-label">Subject <span className="cp-req">*</span></label>
                                        <div className="cp-select-wrap">
                                            <span className="cp-input-icon">🏷️</span>
                                            <select name="subject" className="cp-select" value={form.subject} onChange={handleChange} required>
                                                <option value="">Select a department</option>
                                                <option value="sales">💼 Sales Inquiry</option>
                                                <option value="support">🛠️ Technical Support</option>
                                                <option value="billing">💳 Billing Question</option>
                                                <option value="feedback">💬 Feedback</option>
                                                <option value="other">📋 Other</option>
                                            </select>
                                            <span className="cp-select-chevron">▾</span>
                                        </div>
                                    </div>

                                    <div className="cp-form-group">
                                        <label className="cp-label">Message <span className="cp-req">*</span></label>
                                        <textarea name="message" className="cp-textarea" rows={6} placeholder="Tell us how we can help you..." value={form.message} onChange={handleChange} maxLength={2000} required />
                                        <div className="cp-charcount">{charCount} / 2000</div>
                                    </div>

                                    <div className="cp-form-group cp-checkbox-group">
                                        <label className="cp-checkbox-label">
                                            <input type="checkbox" name="newsletter" checked={form.newsletter} onChange={handleChange} className="cp-checkbox-input" />
                                            <span className="cp-checkbox-box" />
                                            <span className="cp-checkbox-text">Subscribe to our newsletter for WhatsApp automation tips and special offers</span>
                                        </label>
                                    </div>

                                    <button type="submit" className="cp-submit" disabled={sending}>
                                        {sending ? (
                                            <><span className="cp-spinner" /> Sending…</>
                                        ) : (
                                            <>📤 Send Message</>
                                        )}
                                    </button>

                                    <p className="cp-privacy">🔒 Your information is encrypted and never shared with third parties.</p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── DEPARTMENTS ── */}
            <section className="cp-section cp-dept-section">
                <div className="cp-inner">
                    <div className="cp-sec-header cp-animate">
                        <div className="cp-sec-badge">👥 Departments</div>
                        <h2 className="cp-sec-title">Contact the Right Team</h2>
                        <p className="cp-sec-sub">Skip the queue — reach out directly to the team that handles your request</p>
                    </div>
                    <div className="cp-dept-grid">
                        {departments.map((dept, i) => (
                            <div key={i} className={`cp-dept-card cp-animate`} style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className={`cp-dept-icon cp-icon-${dept.color}`}>{dept.icon}</div>
                                <h3 className="cp-dept-title">{dept.title}</h3>
                                <p className="cp-dept-desc">{dept.desc}</p>
                                <div className="cp-dept-contacts">
                                    <a href={`mailto:${dept.email}`} className="cp-dept-link">✉️ {dept.email}</a>
                                    <a href={`tel:${dept.phone.replace(/\s/g, '')}`} className="cp-dept-link">📞 {dept.phone}</a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SELF SERVICE ── */}
            <section className="cp-section cp-self-section">
                <div className="cp-inner">
                    <div className="cp-sec-header cp-animate">
                        <div className="cp-sec-badge">⚡ Self-Service</div>
                        <h2 className="cp-sec-title">Help Yourself, Instantly</h2>
                        <p className="cp-sec-sub">Most answers are one click away — no waiting required</p>
                    </div>
                    <div className="cp-self-grid">
                        {selfService.map((item, i) => (
                            <div key={i} className={`cp-self-card cp-animate`} style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className={`cp-self-icon cp-icon-${item.color}`}>{item.icon}</div>
                                <h3 className="cp-self-title">{item.title}</h3>
                                <p className="cp-self-desc">{item.desc}</p>
                                <button className="cp-self-btn" onClick={() => navigate('/how-it-works')}>Browse →</button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FAQ ── */}
            <section className="cp-section cp-faq-section">
                <div className="cp-inner">
                    <div className="cp-faq-grid">
                        <div className="cp-faq-sidebar cp-animate">
                            <div className="cp-sec-badge">❓ FAQ</div>
                            <h2 className="cp-sec-title">Common Support Questions</h2>
                            <p className="cp-sec-sub" style={{ textAlign: 'left', marginTop: 12 }}>Can't find your answer? Send us a message above.</p>
                            <a href="#contact-form" className="cp-faq-cta">✉️ Send a Message</a>
                        </div>
                                              <div className="cp-faq-list cp-animate">
                            {faqs.map((faq, i) => (
                                <div key={i} className={`cp-faq-item ${openFaq === i ? 'cp-faq-open' : ''}`}>
                                    <button className="cp-faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                        <span>{faq.q}</span>
                                        <span className="cp-faq-chevron">{openFaq === i ? '▲' : '▾'}</span>
                                    </button>
                                    {openFaq === i && (
                                        <div className="cp-faq-answer">{faq.a}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <div className="cp-cta">
                <div className="cp-cta-orb cp-cta-orb-1" />
                <div className="cp-cta-orb cp-cta-orb-2" />
                <span className="cp-cta-eyebrow">🎧 Still Need Help?</span>
                <h2 className="cp-cta-title">Our Team is Standing By</h2>
                <p className="cp-cta-sub">Don't wait — start a live chat or call us right now for instant support.</p>
                <div className="cp-cta-btns">
                    <button className="cp-btn-white" onClick={() => alert('Live chat coming soon!')}>💬 Start Live Chat</button>
                    <a href="tel:+918860993121" className="cp-btn-outline">📞 Call Us Now</a>
                </div>
                <div className="cp-cta-trust">
                    <span>🕐 24/7 available</span>
                    <span>⚡ &lt;2 min response</span>
                    <span>🔒 Secure & private</span>
                    <span>⭐ 5-star rated</span>
                </div>
            </div>

            {/* ── FOOTER ── */}
            <footer className="ct-footer">
                <div className="ct-container">
                    <div className="ct-footer-grid">
                        <div>
                            <div className="ct-nav-brand" onClick={() => navigate('/')} style={{ gap: '2px', cursor: 'pointer' }}>
                                <img src="/logo 1.png" alt="ChatTech24" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
                                <span style={{ color: '#D3D3D3', fontFamily: 'Cooper Black', fontSize: '13px', fontWeight: '700' }}>Chat Tech 24</span>
                            </div>
                            <p className="ct-footer-tagline">AI-powered WhatsApp automation platform for modern businesses.</p>
                            <div className="ct-footer-social">
                                <a href="https://vedvika.com" target="_blank" rel="noopener noreferrer" className="ct-footer-social-link">🌐 Website</a>
                                <Link to="/contact" className="ct-footer-social-link">✉️ Contact</Link>
                            </div>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Product</div>
                            <Link to="/pricing">Pricing</Link>
                            <Link to="/features">Features</Link>
                            <Link to="/services">Services</Link>
                            <Link to="/how-it-works">How It Works</Link>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Company</div>
                            <a href="https://vedvika.com/#about" target="_blank" rel="noopener noreferrer">About Vedvika</a>
                            <Link to="/contact">Contact Us</Link>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Support</div>
                            <Link to="/how-it-works">Documentation</Link>
                            <Link to="/contact">Talk to Sales</Link>
                            <a href="mailto:support@chattech24.com">Email Support</a>
                        </div>
                    </div>
                    <div className="ct-footer-bottom">
                        <p className="ct-footer-copy">
                            © 2026 ChatTech24 by <a href="https://vedvika.com" target="_blank" rel="noopener noreferrer">Vedvika Technologies</a>. All rights reserved.
                        </p>
                        <div className="ct-footer-legal">
                            <a href="https://vedvika.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            <a href="https://vedvika.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                        </div>
                    </div>
                </div>
            </footer>

            <Login isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
        </div>
    );
}

export default ContactPage;